import {
	ChevronRightIcon,
	InformationCircleIcon,
	MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { ColumnDef } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronsUpDown, Loader2, MessageCircleMoreIcon } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'react-query'

import { toast } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'
import * as yup from 'yup'

import { EditIcon, TaskSquareIcon } from '@/assets/icons'
import { Button } from '@/components/ui/button'
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PaginationComponent } from '@/components/ui/pagination'
import {
	ResponsiveModal,
	ResponsiveModalContent,
	ResponsiveModalDescription,
	ResponsiveModalHeader,
	ResponsiveModalTitle,
} from '@/components/ui/responsive-dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import productService from '@/services/product-service'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'

export const ProductsTable = () => {
	const searchableColumns = [
		{ label: 'Produkt-ID', value: 'id' },
		{ label: 'SKU', value: 'sku' },
		{ label: 'Markenname', value: 'brand_name' },
		{ label: 'Titel', value: 'title' },
		{ label: 'User ID', value: 'user_id' },
	]

	const isSmallScreen = useMediaQuery('(max-width: 64rem)')

	const [expandedRows, setExpandedRows] = useState<number[]>([])
	const [filterValue, setFilterValue] = useState('')
	const [searchValue, setSearchValue] = useState('')
	const [isNoteDialogVisible, setIsNoteDialogVisible] = useState<{
		id: number | null
		note: string
	}>({
		id: null,
		note: '',
	})
	const [page, setPage] = useState(1)
	const [selectedColumn, setSelectedColumn] = useState('sku')
	const [isUpdateDialogVisible, setIsUpdateDialogVisible] = useState<Product['id'] | null>(null)
	const [rowsPerPage, setRowsPerPage] = useState(50)
	const [sortColumn, setSortColumn] = useState('')
	const [sortDirection, setSortDirection] = useState('')

	const schema = yup.object<{ price: number }>().shape({
		price: yup
			.number()
			.transform((value, originalValue) => {
				return originalValue === '' ? undefined : value
			})
			.typeError('Der Preis muss eine Zahl sein')
			.required('Preis erforderlich!'),
	})

	const form = useForm<{ price: number }>({
		resolver: yupResolver(schema),
		mode: 'all',
	})

	const { mutate, isLoading: isUpdatingPrice } = useMutation({
		mutationKey: 'update-price',
		mutationFn: ({ productId, price }: { productId: Product['id']; price: number }) =>
			productService.updatePrice(productId, price),
	})

	const { mutate: updateNote, isLoading: isUpdatingNote } = useMutation({
		mutationKey: 'update-note',
		mutationFn: ({ note, id }: { note: string; id: number }) => productService.updateNote(id, note),
		onSuccess: () => {
			toast.success('Hinweis erfolgreich aktualisiert')
			setIsNoteDialogVisible({ note: '', id: null })
			refetch()
		},
		onError: (err: any) => {
			toast.error(
				err?.response?.data?.message ??
					'Hinweis konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
			)
		},
	})

	const { data, refetch, isLoading } = useQuery(
		'fetch-products',
		() =>
			productService.fetchProductsData({
				pageNumber: page.toString(),
				pageSize: rowsPerPage.toString(),
				filterColumn: selectedColumn,
				filterValue,
			}),
		{
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching products')
			},
		},
	)

	useEffect(() => {
		refetch()
	}, [rowsPerPage, page])

	const sortedData = useMemo(() => {
		if (!data) return []
		if (!sortColumn) return data.data

		const getNestedValue = (obj: any, path: string) =>
			path.split('.').reduce((acc, part) => acc?.[part], obj)

		return [...data.data].sort((a, b) => {
			const aValue = getNestedValue(a, sortColumn)
			const bValue = getNestedValue(b, sortColumn)

			if (aValue == null || bValue == null) return 0

			let aVal: any = aValue
			let bVal: any = bValue

			if (aVal instanceof Date) aVal = aVal.getTime()
			if (bVal instanceof Date) bVal = bVal.getTime()

			const aParsed = typeof aVal === 'string' ? Date.parse(aVal) : NaN
			const bParsed = typeof bVal === 'string' ? Date.parse(bVal) : NaN

			if (!isNaN(aParsed) && !isNaN(bParsed)) {
				aVal = aParsed
				bVal = bParsed
			}

			if (typeof aVal === 'number' && typeof bVal === 'number') {
				return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
			}

			if (typeof aVal === 'string' && typeof bVal === 'string') {
				return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
			}

			return 0
		})
	}, [data, sortColumn, sortDirection])

	const { filteredProducts } = useMemo(() => {
		return {
			filteredProducts: sortedData?.filter(curr => {
				if (!searchValue) {
					return true
				}

				const searchLowerCase = searchValue.toLowerCase()
				return curr?.sku?.toLowerCase().includes(searchLowerCase)
			}),
		}
	}, [sortedData, searchValue])

	const toggleRow = (id: number) => {
		setExpandedRows(prev =>
			prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
		)
	}

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortColumn(column)
			setSortDirection('asc')
		}
	}

	const handleNoteSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		const formData = new FormData(event.currentTarget)
		const formJson = Object.fromEntries((formData as any).entries())
		const note = formJson.note
		if (isNoteDialogVisible.id) {
			updateNote({ note: note, id: isNoteDialogVisible.id })
		}
	}

	const onPriceSubmit = (data: { price: number }) => {
		mutate(
			{ price: data.price, productId: isUpdateDialogVisible! },
			{
				onSuccess: res => {
					if (res?.status) {
						toast.success('Preis erfolgreich aktualisiert')
						form.reset()
						setIsUpdateDialogVisible(null)
						refetch()
					}
				},
				onError: (err: any) => {
					toast.error(err?.response?.data?.message)
				},
			},
		)
	}

	const columns: ColumnDef<Product>[] = [
		{
			accessorKey: 'sku',
			header: ({ column }) => <DataTableColumnHeader column={column} title="SKU" />,
			cell: ({ row }) => (
				<div className="flex">
					<div className="w-fit">{row.getValue('sku')}</div>
					<div
						onClick={() =>
							setIsNoteDialogVisible({
								note: row.original.product_notes ?? '',
								id: row.original.id,
							})
						}
						className="cursor-pointer relative rounded-[18px] w-fit pl-1">
						<MessageCircleMoreIcon className="size-4" />
						<div
							className={cn('size-2 rounded-full bg-red-500 absolute top-0 -right-1', {
								hidden: row.original.product_notes === null || row.original.product_notes === '',
							})}
						/>
					</div>
				</div>
			),
		},
		{
			accessorKey: 'brand_name',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Markenname" />,
		},
		{
			accessorKey: 'title',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Titel" />,

			cell: ({ row }) => <div className="text-left max-w-[300px]">{row.getValue('title')}</div>,
		},
		{
			accessorKey: 'description',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Beschreibung" />,
			cell: ({ row }) => (
				<div className="text-left max-w-[300px]">{row.getValue('description')}</div>
			),
		},
		{
			accessorKey: 'quantity',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Menge" />,
		},
		{
			accessorKey: 'price',
			accessorFn: (row: Product) => `€${row.price}`,
			header: ({ column }) => <DataTableColumnHeader column={column} title="Preis" />,
		},
		{
			accessorKey: 'user_id',
			header: ({ column }) => <DataTableColumnHeader column={column} title="User ID" />,
		},
		{
			accessorKey: 'booking',
			accessorFn: (row: Product) =>
				row.booking?.status === '2' || new Date(row.booking.end) < new Date()
					? 'Abgesagt'
					: row.booking?.status === '1'
						? 'Aktiv'
						: 'Inaktiv',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Buchungsstatus" />,
			cell: ({ row }) => (
				<div
					className={cn(
						'w-fit rounded-[18px] p-1.5 px-3',
						row.getValue('booking') === 'Aktiv'
							? 'bg-[#61B9641A] text-[#61B964]'
							: 'bg-[#FFEFF2] text-[#DB5358]',
					)}>
					{row.getValue('booking')}
				</div>
			),
		},
		{
			accessorKey: 'is_active',
			accessorFn: (row: Product) => (row.is_active === null ? 'Sold' : 'Aktiv'),
			header: ({ column }) => <DataTableColumnHeader column={column} title="Artikel Status" />,
		},
		{
			id: 'action-users',
			enableSorting: false,
			header: ({ column }) => <DataTableColumnHeader column={column} title="Aktionen" />,
			cell: ({ row }) => (
				<div className="flex items-center justify-center gap-0.5">
					<Tooltip>
						<TooltipTrigger
							onClick={() => {
								form.reset({
									price: row.original.price,
								})
								setIsUpdateDialogVisible(row.original.id)
							}}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							<EditIcon />
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">Sicht</p>
						</TooltipContent>
					</Tooltip>
				</div>
			),
		},
	]

	const MobileProductsTable = () => {
		return (
			<Table>
				<TableHeader>
					<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
						<TableHead
							align="center"
							onClick={() => handleSort('sku')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								SKU
								{sortColumn === 'sku' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('booking.status')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Status
								{sortColumn === 'booking.status' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('user_id')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								User ID
								{sortColumn === 'user_id' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{filteredProducts?.map((product, index) => (
						<React.Fragment key={product.id.toString() + index.toString()}>
							<TableRow
								className={cn(
									'h-14',
									expandedRows.includes(product.id) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
								)}
								onClick={() => toggleRow(product.id)}>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(product.id),
									})}>
									<div className="flex items-center">
										<ChevronRightIcon
											className={cn(
												'size-3 shrink-0 -mt-0.5',
												expandedRows.includes(product.id)
													? 'rotate-90 text-secondary'
													: 'text-muted-foreground',
											)}
										/>
										{product.sku}
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-[10px]', {
										'text-muted-foreground': !expandedRows.includes(product.id),
									})}>
									<div
										className={cn(
											'text-[10px] w-fit p-1.5 px-2 rounded-[18px]',
											product.booking?.status === '1'
												? 'bg-[#61B9641A] text-[#61B964]'
												: 'bg-[#FFEFF2] text-[#DB5358]',
										)}>
										{product.booking?.status === '2' || new Date(product.booking.end) < new Date()
											? 'Abgesagt'
											: product.booking?.status === '1'
												? 'Aktiv'
												: 'Inaktiv'}
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(product.id),
									})}>
									{product.user_id}
								</TableCell>
							</TableRow>
							{expandedRows.includes(product.id) && (
								<TableRow
									className={cn(
										'mt-2',
										!expandedRows.includes(product.id)
											? '!border-b-0'
											: 'border-b border-[#F4F4F4]',
									)}>
									<TableCell className="pt-0" colSpan={3}>
										<div className="px-3 flex flex-col gap-4">
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Produkthinweise
												</h6>
												<p
													onClick={() =>
														setIsNoteDialogVisible({
															note: product.product_notes ?? '',
															id: product.id,
														})
													}
													className="text-secondary relative w-fit cursor-pointer text-xs font-medium">
													<MessageCircleMoreIcon className="size-3" />
													<div
														className={cn(
															'size-1.5 rounded-full bg-red-500 absolute top-1 right-2',
															{
																hidden:
																	product.product_notes === null || product.product_notes === '',
															},
														)}
													/>
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Markenname
												</h6>
												<p className="text-secondary text-xs font-medium">{product.brand_name}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Titel</h6>
												<p className="text-secondary max-w-[200px] text-right text-xs font-medium">
													{product.title}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Beschreibung
												</h6>
												<p className="text-secondary max-w-[200px] text-right text-xs font-medium">
													{product.description}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Menge</h6>
												<p className="text-secondary text-xs font-medium">{product.quantity}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Preis</h6>
												<p className="text-secondary text-xs font-medium">€{product.price}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">User ID</h6>
												<p className="text-secondary text-xs font-medium">{product.user_id}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Artikel Status
												</h6>
												<p className="text-secondary text-xs font-medium">
													{product.is_active === null ? 'Sold' : 'Aktiv'}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<div className="flex items-center gap-1">
													<InformationCircleIcon className="text-muted-foreground size-3" />
													<h6 className="text-muted-foreground text-[10px] font-medium">Aktion</h6>
												</div>
												<div className="flex items-center gap-1">
													<div
														onClick={() => {
															setIsUpdateDialogVisible(product.id)
															form.reset({
																price: product.price,
															})
														}}
														className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
														<EditIcon className="shrink-0 max-lg:size-3" />
													</div>
												</div>
											</div>
										</div>
									</TableCell>
								</TableRow>
							)}
						</React.Fragment>
					))}
				</TableBody>
			</Table>
		)
	}

	return (
		<div className="rounded-[20px] bg-white max-lg:p-4 max-lg:pr-3 lg:px-4 lg:py-6 flex flex-col">
			<ResponsiveModal
				open={isNoteDialogVisible.id !== null}
				onOpenChange={() => setIsNoteDialogVisible({ note: '', id: null })}>
				<ResponsiveModalContent
					crossIconClassName="top-3 lg:top-4.5"
					className="!px-6 !py-4 !max-w-[550px] gap-0">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Produkthinweise
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Produkthinweise
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<form onSubmit={handleNoteSubmit} className="mt-3 lg:mt-4 flex flex-col">
						<Textarea
							defaultValue={isNoteDialogVisible.note}
							className="resize-none max-lg:placeholder:text-sm min-h-[117px]"
							name="note"
							placeholder="Notiz hinzufügen"
						/>
						<Button
							variant="secondary"
							className="mt-6 w-full lg:h-[44px] rounded-[12px] font-extrabold text-white">
							{isUpdatingNote && <Loader2 className="animate-spin" />}
							{isUpdatingNote ? 'Bitte warten' : 'Speichern'}
						</Button>
					</form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<ResponsiveModal
				open={isUpdateDialogVisible !== null}
				onOpenChange={() => {
					form.reset()
					setIsUpdateDialogVisible(null)
				}}>
				<ResponsiveModalContent className="gap-0 lg:max-w-xl">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Preis aktualisieren
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Preis aktualisieren
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onPriceSubmit)}>
							<div className="mt-6">
								<FormField
									control={form.control}
									name="price"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-muted-foreground">
												Preis <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="Preis" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<Button
								variant="secondary"
								className="mt-10 w-full lg:h-[61px] rounded-[12px] font-extrabold text-white">
								{isUpdatingPrice && <Loader2 className="animate-spin" />}
								{isUpdatingPrice ? 'Bitte warten' : 'Preis ändern'}
							</Button>
						</form>
					</Form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<div className="flex items-center gap-2">
				<div className="lg:hidden rounded-xl border border-[#EEEFF0] size-10 flex items-center justify-center">
					<TaskSquareIcon className="text-secondary" />
				</div>
				<h1 className="text-muted-foreground lg:text-secondary text-xs lg:text-2xl font-medium lg:font-extrabold">
					Produkt Liste
				</h1>
			</div>

			<div className="flex max-lg:mt-5 mt-6 flex-col gap-3 lg:gap-4">
				<div className="flex lg:flex-row gap-3 lg:gap-4 flex-col lg:items-center max-lg:justify-between">
					<div className="grid grid-cols-2 items-center gap-[9px] lg:hidden">
						<div className="relative flex items-center">
							<Input
								value={searchValue}
								onChange={e => setSearchValue(e.target.value)}
								className="!pl-[34px] h-10 text-xs"
								placeholder="Suche..."
							/>
							<MagnifyingGlassIcon className="size-4 text-muted-foreground absolute left-2" />
						</div>
						<Select
							value={rowsPerPage.toString()}
							onValueChange={value => setRowsPerPage(Number(value))}>
							<SelectTrigger className="w-full h-10 text-xs">
								<SelectValue placeholder="zeigen 50" />
							</SelectTrigger>
							<SelectContent>
								{[50, 80, 100, 200, 500].map(pageSize => (
									<SelectItem key={pageSize} value={pageSize.toString()}>
										zeigen {pageSize}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
				<div className="lg:hidden flex flex-col gap-3">
					<Select value={selectedColumn} onValueChange={setSelectedColumn}>
						<SelectTrigger className="h-10 text-xs w-full">
							<SelectValue placeholder="Kategorie" />
						</SelectTrigger>
						<SelectContent>
							{searchableColumns.map(column => (
								<SelectItem key={column.value} value={column.value}>
									{column.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<div className="flex lg:hidden items-center gap-2">
						<Input
							value={filterValue}
							onChange={e => setFilterValue(e.target.value)}
							className="h-10 text-xs"
							placeholder="Bezeichnung eingeben"
						/>
						<Button
							onClick={() => refetch()}
							variant="secondary"
							className="w-[73px] py-3 text-xs font-medium bg-secondary rounded-lg">
							Suche
						</Button>
					</div>
				</div>
				<div className="flex max-lg:hidden items-center gap-3">
					<Select value={selectedColumn} onValueChange={setSelectedColumn}>
						<SelectTrigger className="flex-1">
							<SelectValue placeholder="Kategorie" />
						</SelectTrigger>
						<SelectContent>
							{searchableColumns.map(column => (
								<SelectItem key={column.value} value={column.value}>
									{column.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Input
						value={filterValue}
						onChange={e => setFilterValue(e.target.value)}
						className="flex-1"
						placeholder="Bezeichnung eingeben"
					/>
					<Button
						onClick={() => refetch()}
						variant="secondary"
						className="w-[90px] font-medium bg-secondary rounded-xl">
						Suche
					</Button>
				</div>
			</div>
			<div className="flex max-lg:hidden items-center justify-between mt-6">
				<div className="flex items-center gap-4">
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => setRowsPerPage(Number(value))}>
						<SelectTrigger className="w-[127px]">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-8">
					<PaginationComponent
						currentPage={page}
						totalPages={data?.pagination?.totalPages ?? 1}
						onPageChange={setPage}
					/>
					<div className="relative flex items-center">
						<Input
							value={searchValue}
							onChange={e => setSearchValue(e.target.value)}
							className="!pl-[38px]"
							placeholder="Suche..."
						/>
						<MagnifyingGlassIcon className="size-5 text-muted-foreground absolute left-2" />
					</div>
				</div>
			</div>
			<div className="mt-4">
				{isSmallScreen ? (
					<MobileProductsTable />
				) : (
					<DataTable columns={columns} data={filteredProducts ?? []} isLoading={isLoading} />
				)}
			</div>
			<div className="mt-6 lg:mt-16 flex justify-center">
				<PaginationComponent
					currentPage={page}
					totalPages={data?.pagination.totalPages ?? 1}
					onPageChange={setPage}
				/>
			</div>
		</div>
	)
}
